import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import "./index.css";
import "./i18n/config";

const CHUNK_RETRY_KEY = "arrowlink_chunk_retry_at";

const getErrorMessage = (errorLike: unknown): string => {
  if (errorLike instanceof Error) return errorLike.message;
  if (typeof errorLike === "string") return errorLike;
  if (errorLike && typeof errorLike === "object" && "message" in errorLike) {
    return String((errorLike as { message?: unknown }).message ?? "");
  }
  return "";
};

const isChunkLoadError = (errorLike: unknown): boolean => {
  const message = getErrorMessage(errorLike).toLowerCase();
  return (
    message.includes("failed to fetch dynamically imported module") ||
    message.includes("importing a module script failed") ||
    message.includes("chunkloaderror") ||
    message.includes("loading chunk")
  );
};

const recoverChunkOnce = () => {
  const lastRetryAt = Number(sessionStorage.getItem(CHUNK_RETRY_KEY) ?? "0");
  const retryWindowMs = 15000;

  if (Date.now() - lastRetryAt < retryWindowMs) return;

  sessionStorage.setItem(CHUNK_RETRY_KEY, String(Date.now()));
  window.location.reload();
};

window.addEventListener("error", (event) => {
  if (isChunkLoadError(event.error ?? event.message)) {
    recoverChunkOnce();
  }
});

window.addEventListener("unhandledrejection", (event) => {
  if (isChunkLoadError(event.reason)) {
    event.preventDefault();
    recoverChunkOnce();
  }
});

createRoot(document.getElementById("root")!).render(<App />);

// Fade out the inline branded loader ONLY after React has painted real
// content. We wait for two animation frames (so styles + first paint have
// committed) plus a minimum 600ms so the brand mark animation always
// completes — never a jarring flash on fast devices and never a "blank
// then content" swap on slow mobile.
const dismissInitialLoader = () => {
  const start = performance.now();
  const minVisibleMs = 600;
  const finalize = () => {
    document.documentElement.classList.add("app-ready");
    // Remove from DOM after CSS fade completes
    window.setTimeout(() => {
      document.getElementById("initial-loader")?.remove();
    }, 400);
  };
  const wait = Math.max(0, minVisibleMs - (performance.now() - start));
  requestAnimationFrame(() =>
    requestAnimationFrame(() => window.setTimeout(finalize, wait)),
  );
};

if (document.readyState === "complete") {
  dismissInitialLoader();
} else {
  window.addEventListener("load", dismissInitialLoader, { once: true });
}
